import React, { useState } from 'react';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { centsToDollarsDecimal, dollarsToCents, removeNonDigit } from '@common/paymentsUtils/currencyUtils';
import { MOVE_IN_COSTS_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import { getMoveInCostsTotalLimitError, isMoveInCostsNoteLengthValid, validateProratedRentInputs } from '../validation';
import userPersonas from '@common/constants/userPersonas';
import {
  AdornedInput,
  Box,
  Button,
  FieldSet,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Textarea,
} from '@zillow/constellation-10';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import { CharacterCountFormHelp, useCharacterCount } from '@zg-rentals/visual-cohesion';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  editMode: boolean;
  fromDashboard: boolean;
  proratedRentPaymentAmount: number;
  proratedRentNotes: string;
  proratedRentStartsOpen: boolean;
  moveInCostsPaymentLimits: number;
  totalPaymentAmount: number;
  showModalNotification: (args: string) => Record<string, any>;
  individualLimit: number;
  proratedRentFromLease: number;
  pageVisited: Set<number>;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  togglePrevPage: () => void;
}

const ProratedRentPage = ({
  saveStateToReduxStore,
  editMode = false,
  fromDashboard = false,
  proratedRentPaymentAmount,
  proratedRentNotes,
  proratedRentStartsOpen,
  moveInCostsPaymentLimits,
  totalPaymentAmount: initialTotalPaymentAmount,
  proratedRentFromLease,
  individualLimit,
  pageVisited,
  currentProperty,
  user,
  onClose,
  togglePrevPage,
}: Props) => {
  const initialIsOpen =
    !editMode && !pageVisited?.has(2) && proratedRentFromLease && proratedRentPaymentAmount === 0
      ? true
      : proratedRentStartsOpen;

  const hasAnsweredBefore = editMode || Boolean(pageVisited?.has(2));
  const [isOpen, setIsOpen] = useState<boolean | null>(initialIsOpen ? true : hasAnsweredBefore ? false : null);
  const [totalPaymentAmount] = useState(
    proratedRentPaymentAmount === 0 ? initialTotalPaymentAmount : initialTotalPaymentAmount - proratedRentPaymentAmount,
  );

  const [moveInCosts, setMoveInCosts] = useState({
    moveInCostsPaymentType: 'Prorated Rent',
    moveInCostsNotes: proratedRentNotes,
    moveInCostsPaymentAmount:
      proratedRentPaymentAmount === 0
        ? !editMode && !pageVisited?.has(2) && proratedRentFromLease
          ? String(centsToDollarsDecimal(proratedRentFromLease))
          : ''
        : String(centsToDollarsDecimal(proratedRentPaymentAmount)),
  });

  const [errors, setErrors] = useState({
    moveInCostsPaymentType: '',
    moveInCostsNotes: '',
    moveInCostsPaymentAmount: '',
  });

  const skipProratedRentPage = async (): Promise<any> => {
    setIsOpen(false);

    IreneOnboardingCSEvents.emitSkipProratedRentEvent({ currentProperty, user });

    await saveStateToReduxStore({
      proratedRentSettings: {
        proratedRentPaymentAmount: 0,
        proratedRentNotes: '',
        proratedRentStartsOpen: false,
      },
    });
  };

  const handleNotesChange = (e): void => {
    const key = e.target.dataset.statekey;
    setMoveInCosts((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const { isFocused, onFocus, onBlur } = useCharacterCount();

  const handleAmountChange = (e): void => {
    e.preventDefault();
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    const numericValue = removeNonDigit(value);

    setMoveInCosts((prev) => ({ ...prev, moveInCostsPaymentAmount: numericValue }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleSaveState = async (): Promise<any> => {
    const validInputs = validateProratedRentInputs({
      moveInCosts,
      individualLimit,
      handleErrors: (errors) => {
        setErrors(errors);
      },
    });

    if (!validInputs) {
      return;
    }

    const moveInCostsPaymentAmount = parseInt(dollarsToCents(moveInCosts.moveInCostsPaymentAmount), 10);

    if (totalPaymentAmount + moveInCostsPaymentAmount > moveInCostsPaymentLimits) {
      setErrors((prev) => ({
        ...prev,
        moveInCostsPaymentAmount: getMoveInCostsTotalLimitError(moveInCostsPaymentLimits),
      }));
      return;
    }

    IreneOnboardingCSEvents.emitContinueFromProratedRentEvent({
      currentProperty,
      user,
    });
    await saveStateToReduxStore({
      proratedRentSettings: {
        proratedRentPaymentAmount: moveInCostsPaymentAmount,
        proratedRentNotes: moveInCosts.moveInCostsNotes,
        proratedRentStartsOpen: isOpen === true,
      },
    });
  };

  const renderOpenedSection = (): JSX.Element => (
    <>
      <FormField
        css={{ maxWidth: '36rem' }}
        error={Boolean(errors.moveInCostsPaymentAmount)}
        required
        control={
          <AdornedInput
            fluid
            input={
              <AdornedInput.Input
                min="0"
                type="number"
                className="fs-exclude input-caret-hide"
                value={!moveInCosts.moveInCostsPaymentAmount ? '' : moveInCosts.moveInCostsPaymentAmount}
                onChange={handleAmountChange}
                onBlur={handleAmountChange}
                onFocus={handleAmountChange}
                data-statekey="moveInCostsPaymentAmount"
              />
            }
            size="md"
            startAdornment={<AdornedInput.Adornment id="start-adornment">$</AdornedInput.Adornment>}
          />
        }
        description={errors.moveInCostsPaymentAmount && <FormHelp>{errors.moveInCostsPaymentAmount}</FormHelp>}
        label={<Label>Amount</Label>}
      />

      <FormField
        css={{ maxWidth: '36rem' }}
        error={!isMoveInCostsNoteLengthValid(moveInCosts.moveInCostsNotes)}
        optional
        control={
          <Textarea
            data-statekey="moveInCostsNotes"
            className="fs-exclude"
            css={{ resize: 'vertical' }}
            value={moveInCosts.moveInCostsNotes}
            onChange={handleNotesChange}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        }
        description={
          <CharacterCountFormHelp
            currentLength={moveInCosts.moveInCostsNotes.length}
            maxLength={MOVE_IN_COSTS_NOTE_MAX_LENGTH}
            isFocused={isFocused}
            label="Note"
          />
        }
        label={<Label>Note</Label>}
      />
    </>
  );

  const renderMainSection = (): JSX.Element => (
    <Box
      css={{
        display: 'grid',
        gridTemplateRows: 'auto',
        gap: 'default',
      }}
    >
      <FieldSet required legend={<Legend size="lg">Would you like to collect prorated rent?</Legend>}>
        <LabeledControl
          control={
            <Radio
              name="prorated-rent"
              value="yes"
              checked={isOpen === true}
              onChange={(): void => {
                setIsOpen(true);
              }}
            />
          }
          label={<Label>Yes</Label>}
        />
        <LabeledControl
          control={
            <Radio name="prorated-rent" value="no" checked={isOpen === false} onChange={(): void => setIsOpen(false)} />
          }
          label={<Label>No</Label>}
        />
      </FieldSet>
      {isOpen && renderOpenedSection()}
    </Box>
  );

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={<FaqComponent faqs={FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.PRORATED_RENT_PAGE} />}
      onRequestClose={onClose}
      header={fromDashboard ? 'Move-in costs' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {renderMainSection()}
        </Box>
      }
      backAction={{ onClick: togglePrevPage }}
      footerAriaLabel="Prorated rent actions"
      footerEnd={
        <Button emphasis="filled" disabled={isOpen === null} onClick={isOpen ? handleSaveState : skipProratedRentPage}>
          Continue
        </Button>
      }
    />
  );
};

export default ProratedRentPage;
